"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Row, Col, Form, Button, Card, Container, Badge } from "react-bootstrap"
import { toast } from "react-hot-toast"
import { Title } from "../../../../Templates/Title"
import GestorIcon from "../../../../assets/GestorIcon"
import { FaCheck, FaImage, FaPalette, FaPhone, FaTag, FaUser, FaExternalLinkAlt } from "react-icons/fa"
import { openImage } from "../../../../Utils/images"
import { getPreferences, updatePreferences } from "../../../API/Preferences/Preferences"
import type { PreferencesResponse } from "../../../API/Preferences/models"
import { useTestAdminToken } from "../../../../pages/Dashboard/Hooks/useTestAdminToken"
import { TbAlignBoxLeftTop, TbWorld } from "react-icons/tb"
import { WelcomeModal } from "./WelcomeModal"
import { BsChatText } from "react-icons/bs"
import { BiEnvelope } from "react-icons/bi"
import { LiaFileInvoiceDollarSolid } from "react-icons/lia"
export const Preferences = () => {
  const [preferences, setPreferences] = useState<PreferencesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const { adminInfo } = useTestAdminToken()
  const [showConfigWelcomeModal, setShowConfigWelcomeModal] = useState(false)

  const fetchPreferences = async () => {
    try {
      setLoading(true)
      const res = await getPreferences()
      if (res?.id) {
        setPreferences({
          ...res,
          logo: `${process.env.REACT_APP_API}${res.logo}` || "",
          content: `${process.env.REACT_APP_API}${res.content}` || "",
        })
      }
    } catch (error) {
      toast.error("Error al cargar las preferencias")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setLoading(true)
      console.log(preferences)

      const formData = new FormData()
      formData.append("name", preferences?.name || "")
      formData.append("phone", preferences?.phone || "")
      formData.append("showPromo", preferences?.showPromo?.toString() || "false")
      formData.append("active", preferences?.active?.toString() || "false")
      formData.append("mainColor", preferences?.mainColor || "#000000")
      formData.append("description", preferences?.description || "")
      formData.append("showWelcomeModal", preferences?.showWelcomeModal?.toString() || "false")
      formData.append("welcomeChatMessage", preferences?.welcomeChatMessage?.toString() || "")
      formData.append("useAutomaticMails", preferences?.useAutomaticMails?.toString() || "false")
      formData.append("useLastProofOfLoad", preferences?.useLastProofOfLoad?.toString() || "false")
      if (imageFile) {
        formData.append("content", imageFile)
      }

      if (logoFile) {
        formData.append("logo", logoFile)
      }

      if (preferences) {
        await updatePreferences(formData)
        toast.success("Preferencias actualizadas correctamente")
      }
    } catch (error) {
      toast.error("Error al actualizar las preferencias")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target

    setPreferences((prev: any) => ({
      ...prev!,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  useEffect(() => {
    fetchPreferences()
  }, [])

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    )

  return (
    <Container fluid className="px-0">
      {showConfigWelcomeModal && (
        <WelcomeModal
          onClose={() => setShowConfigWelcomeModal(false)}
          content={preferences?.welcomeModalContent || ""}
          setContent={(content: string) => setPreferences((prev: any) => ({ ...prev!, welcomeModalContent: content }))}
        />
      )}

      <div className="mt-4 rounded text-light">
        <Title
          title="Preferencias de la web Gestor"
          icon={<GestorIcon size={40} />}
          subtitle="Configura todo lo relacionado a la web Gestor y tus datos públicos"
        />

        <Card className="mb-4 shadow-sm border-0">
          <Card.Body className="p-0">
            <Form onSubmit={handleSubmit}>
              {/* Web URL Banner */}
              <div className="bg-dark p-3 rounded-top">
                <div className="d-flex align-items-center gap-3 p-3 bg-dark bg-opacity-50 rounded-3 border border-secondary">
                  <TbWorld size={40} className="text-primaryy" />
                  <div>
                    <h5 className="mb-1">Tu web Gestor</h5>
                    <div className="d-flex align-items-center gap-2">
                      <a
                        className="text-decoration-none text-primaryy"
                        href={`https://${adminInfo?.gestorUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {adminInfo?.gestorUrl} <FaExternalLinkAlt size={12} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-4">
                <Row>
                  {/* Left Column */}
                  <Col lg={6} className="mb-4">
                    <div className="bg-dark bg-opacity-25 p-4 rounded-3 h-100">
                      <h4 className="border-bottom pb-2 mb-4 text-primaryy">Información General</h4>

                      <Form.Group className="mb-4">
                        <Title
                          title="Nombre público"
                          subtitle="Nombre con el que quieres mostrarte en la web Gestor"
                          icon={<FaUser size={22} className="text-primary" />}
                        />
                        <Form.Control
                          type="text"
                          maxLength={50}
                          placeholder="Nombre"
                          name="name"
                          value={preferences?.name || ""}
                          onChange={handleChange}
                          className="bg-dark text-light border-secondary"
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Title
                          title="Teléfono"
                          subtitle="Un teléfono de contacto para tus jugadores. Codigo de país + número"
                          icon={<FaPhone size={22} className="text-primary" />}
                        />
                        <Form.Control
                          type="text"
                          name="phone"
                          maxLength={25}
                          placeholder="Ejemplo: 541133333333"
                          value={preferences?.phone || ""}
                          onChange={handleChange}
                          className="bg-dark text-light border-secondary"
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Title
                          title="Mensaje de Bienvenida (Chat)"
                          subtitle="Se enviará mediante el chat a quien entre a la pantalla de registro como bienvenida."
                          icon={<BsChatText size={22} className="text-primary" />}
                        />
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="welcomeChatMessage"
                          maxLength={500}
                          placeholder="Ejemplo: ¡Bienvenido! ¿Cómo te puedo ayudar?"
                          value={preferences?.welcomeChatMessage || ""}
                          onChange={handleChange}
                          className="bg-dark text-light border-secondary"
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  {/* Right Column */}
                  <Col lg={6} className="mb-4">
                    <div className="bg-dark bg-opacity-25 p-4 rounded-3 h-100">
                      <h4 className="border-bottom pb-2 mb-4 text-primaryy">Apariencia</h4>
                      <Form.Group className="w">
                        <Title
                          title="Logo"
                          subtitle="Sube el logo de tu empresa"
                          icon={<FaImage size={22} className="text-primary" />}
                        />
                        <div className="d-flex align-items-center gap-3 w-100">
                          <Button
                            variant="outline-primary"
                            className="d-flex align-items-center gap-2 w-100"
                            onClick={() => document.getElementById("logoFileInput")?.click()}
                          >
                            <FaImage size={16} />
                            {preferences?.logo ? "Cambiar Logo" : "Subir Logo"}
                          </Button>

                          {preferences?.logo && (
                            <div
                              role="button"
                              onClick={() => openImage(preferences?.logo || "")}
                              className="border border-secondary rounded p-1"
                            >
                              <img
                                src={preferences?.logo || "/placeholder.svg"}
                                alt="LogoOwner"
                                className="rounded"
                                width={50}
                                height={50}
                                style={{ objectFit: "contain" }}
                              />
                            </div>
                          )}

                          <input
                            type="file"
                            id="logoFileInput"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                setLogoFile(file)
                                const previewUrl = URL.createObjectURL(file)
                                setPreferences((prev: any) => ({
                                  ...prev,
                                  logo: previewUrl,
                                }))
                              }
                            }}
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="my-4">
                        <Title
                          title="Imágen de fondo"
                          subtitle="Sube la imágen de fondo que se verá en la web"
                          icon={<FaImage size={22} className="text-primary" />}
                        />
                        <div className="d-flex align-items-center gap-3">
                          <Button
                            variant="outline-primary"
                            className="d-flex align-items-center gap-2 w-100"
                            onClick={() => document.getElementById("contentFileInput")?.click()}
                          >
                            <FaImage size={16} />
                            {preferences?.content ? "Cambiar Imágen" : "Subir Imágen"}
                          </Button>

                          {preferences?.content && (
                            <div
                              role="button"
                              onClick={() => openImage(preferences?.content || "")}
                              className="border border-secondary rounded p-1"
                            >
                              <img
                                src={preferences?.content || "/placeholder.svg"}
                                alt="content"
                                className="rounded"
                                width={50}
                                height={50}
                                style={{ objectFit: "cover" }}
                              />
                            </div>
                          )}

                          <input
                            type="file"
                            id="contentFileInput"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                setImageFile(file)
                                const previewUrl = URL.createObjectURL(file)
                                setPreferences((prev: any) => ({
                                  ...prev,
                                  content: previewUrl,
                                }))
                              }
                            }}
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Title
                          title="Color Principal"
                          subtitle="Elije un color para el tema principal de la web"
                          icon={<FaPalette size={22} className="text-primary" />}
                        />
                        <div className="d-flex align-items-center gap-3">
                          <Form.Control
                            type="color"
                            name="mainColor"
                            value={preferences?.mainColor || "#000000"}
                            onChange={handleChange}
                            className="bg-dark text-light border-secondary w-50"
                            style={{ width: "80px", height: "40px" }}
                          />
                          <span className="text-light">{preferences?.mainColor || "#000000"}</span>
                        </div>
                      </Form.Group>
                    </div>
                  </Col>

                  {/* Configuration Options */}
                  <Col md={12} className="mb-4">
                    <div className="bg-dark bg-opacity-25 p-4 rounded-3">
                      <h4 className="border-bottom pb-2 mb-4 text-primaryy">Configuración</h4>

                      <Row>
                        <Col md={6} className="mb-4">
                          <div className="d-flex align-items-start gap-3 bg-dark bg-opacity-50 p-3 rounded-3 h-100">
                            <div className="mt-1">
                              <TbAlignBoxLeftTop size={24} className="text-primary" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Modal de Bienvenida</h5>
                                <Form.Check
                                  role="button"
                                  type="switch"
                                  name="showWelcomeModal"
                                  checked={preferences?.showWelcomeModal || false}
                                  onChange={handleChange}
                                  className="text-light"
                                />
                              </div>
                              <p className="text-secondary small mb-3">
                                Si está activado, se mostrará un modal de bienvenida al jugador al momento de
                                registrarse
                              </p>
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <Badge bg={preferences?.showWelcomeModal ? "success" : "secondary"}>
                                  {preferences?.showWelcomeModal ? "Activado" : "Desactivado"}
                                </Badge>
                                <Button
                                  onClick={() => setShowConfigWelcomeModal(!showConfigWelcomeModal)}
                                  variant={preferences?.showWelcomeModal ? "outline-primary" : "outline-secondary"}
                                  disabled={!preferences?.showWelcomeModal}
                                  size="sm"
                                >
                                  {preferences?.showWelcomeModal
                                    ? "Configurar Modal de Bienvenida"
                                    : "El Modal de Bienvenida está desactivado"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Col>

                        <Col md={6} className="mb-4">
                          <div className="d-flex align-items-start gap-3 bg-dark bg-opacity-50 p-3 rounded-3 h-100">
                            <div className="mt-1">
                              <FaCheck size={24} className="text-primary" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Gestor Activo</h5>
                                <Form.Check
                                  role="button"
                                  type="switch"
                                  name="active"
                                  checked={preferences?.active || false}
                                  onChange={handleChange}
                                  className="text-light"
                                />
                              </div>
                              <p className="text-secondary small">
                                Si la opción está activada, podrás recibir nuevas solicitudes de jugadores
                              </p>
                              <Badge bg={preferences?.active ? "success" : "secondary"}>
                                {preferences?.active ? "Activado" : "Desactivado"}
                              </Badge>
                            </div>
                          </div>
                        </Col>

                        <Col md={6} className="mb-4">
                          <div className="d-flex align-items-start gap-3 bg-dark bg-opacity-50 p-3 rounded-3 h-100">
                            <div className="mt-1">
                              <FaTag size={24} className="text-primary" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Mostrar Promociones</h5>
                                <Form.Check
                                  role="button"
                                  type="switch"
                                  name="showPromo"
                                  checked={preferences?.showPromo || false}
                                  onChange={handleChange}
                                  className="text-light"
                                />
                              </div>
                              <p className="text-secondary small">
                                Si la opción está activada, podrás mostrar las promociones en la web
                              </p>
                              <Badge bg={preferences?.showPromo ? "success" : "secondary"}>
                                {preferences?.showPromo ? "Activado" : "Desactivado"}
                              </Badge>
                            </div>
                          </div>
                        </Col>

                        <Col md={6} className="mb-4">
                          <div className="d-flex align-items-start gap-3 bg-dark bg-opacity-50 p-3 rounded-3 h-100">
                            <div className="mt-1">
                              <BiEnvelope size={24} className="text-primary" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Mails Automáticos</h5>
                                <Form.Check
                                  role="button"
                                  type="switch"
                                  name="useAutomaticMails"
                                  checked={preferences?.useAutomaticMails || false}
                                  onChange={handleChange}
                                  className="text-light"
                                />
                              </div>
                              <p className="text-secondary small">
                                Si la opción está activada, se enviarán mails automáticos a los jugadores al momento de
                                registrarse
                              </p>
                              <Badge bg={preferences?.useAutomaticMails ? "success" : "secondary"}>
                                {preferences?.useAutomaticMails ? "Activado" : "Desactivado"}
                              </Badge>
                            </div>
                          </div>
                        </Col>

                        <Col md={6} className="mb-4">
                          <div className="d-flex align-items-start gap-3 bg-dark bg-opacity-50 p-3 rounded-3 h-100">
                            <div className="mt-1">
                              <LiaFileInvoiceDollarSolid size={24} className="text-primary" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Solicitar último comprobante de carga</h5>
                                <Form.Check
                                  role="button"
                                  type="switch"
                                  name="useLastProofOfLoad"
                                  checked={preferences?.useLastProofOfLoad || false}
                                  onChange={handleChange}
                                  className="text-light"
                                />
                              </div>
                              <p className="text-secondary small">
                                Si la opción está activada, se pedirá el último comprobante de carga al jugador al solicitar un retiro.
                              </p>
                              <Badge bg={preferences?.useLastProofOfLoad ? "success" : "secondary"}>
                                {preferences?.useLastProofOfLoad ? "Activado" : "Desactivado"}
                              </Badge>
                            </div>
                          </div>
                        </Col>

                      </Row>
                    </div>
                  </Col>
                </Row>

                {/* Submit Button */}
                <div className="d-flex justify-content-end mt-4">
                  <Button type="submit" variant="primary" disabled={loading} className="px-4 py-2" size="lg">
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Guardando...
                      </>
                    ) : (
                      <>Guardar Cambios</>
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </Container >
  )
}
